import React, { Fragment, ReactNode } from 'react';
import { Breakpoint } from 'react-socks';
import { common, query } from '@kurtosys/ksys-app-template';
import { observer } from 'mobx-react';

import { InjectedStyledComponent, isArrayLike, ResponsiveComponentBase } from '../../../shared/index.js';

import { GridElement } from './models/GridElement.js';
import { IGrid } from './models/IGrid.js';
import { IGridColumn } from './models/IGridColumn.js';
import { IGridComponent } from './models/IGridComponent.js';
import { IGridComponentReference } from './models/IGridComponentReference.js';
import { IGridConfiguration } from './models/IGridConfiguration.js';
import { IGridElementBase } from './models/IGridElementBase.js';
import { IGridElementRepeater } from './models/IGridElementRepeater.js';
import { IGridElementRepeaterInput } from './models/IGridElementRepeaterInput.js';
import { IGridElementRepetition } from './models/IGridElementRepetition.js';
import { IGridProps } from './models/IGridProps.js';
import { IGridRow } from './models/IGridRow.js';
import { IGridState } from './models/IGridState.js';
import Column from './styledComponents/Column.js';
import ComponentWrapper from './styledComponents/Component.js';
import Row from './styledComponents/Row.js';
import Wrapper from './styledComponents/Wrapper.js';
import { GRID_CATEGORY_KEY, GRID_COMPONENT_KEY } from './Grid.meta.js';

export class Grid extends ResponsiveComponentBase<IGridProps, IGridState, IGridConfiguration> {
	categoryKey = GRID_CATEGORY_KEY;
	componentKey = GRID_COMPONENT_KEY;

	constructor(props: IGridProps) {
		super(props);
		this.getComponentProps = this.getComponentProps.bind(this);
		if (props.loadUiIds) {
			this.loadUiIds(props.grid);
		}
	}
	getUiId = (...params: (string | undefined)[]) => {
		return params.filter((param) => param !== undefined).join('_');
	};
	loadUiIds = (grid?: IGrid, parentUiId?: string) => {
		if (grid) {
			if (!grid.uiId) {
				grid.uiId = this.getUiId(parentUiId, common.commonUtils.generateGuid());
			}
			grid.parentUiId = parentUiId;
			if (grid.rows) {
				grid.rows.forEach((row, rowIndex) => {
					if (!row.uiId) {
						row.uiId = this.getUiId(grid.uiId, `row-${rowIndex}`);
					}
					row.parentUiId = grid.uiId;
					if (row.columns) {
						row.columns.forEach((column, columnIndex) => {
							if (!column.uiId) {
								column.uiId = this.getUiId(row.uiId, `column-${columnIndex}`);
							}
							column.parentUiId = row.uiId;
							if (column.components) {
								column.components.forEach((component, componentIndex) => {
									if (!component.uiId) {
										component.uiId = this.getUiId(column.uiId, `component-${componentIndex}`);
									}
									component.parentUiId = column.uiId;
									if (component.grid) {
										this.loadUiIds(component.grid, component.uiId);
									}
								});
							}
						});
					}
				});
			}
		}
	};
	getComponentProps(component?: IGridComponent, componentReference?: IGridComponentReference, responseContext?: any) {
		if (component && componentReference) {
			const referenceProps = this.mergeComponentPropsAndQueries(componentReference, responseContext);
			const props = this.mergeComponentPropsAndQueries(component, responseContext);
			return { props, referenceProps };
		}
	}
	mergeComponentPropsAndQueries = (component?: IGridComponent | IGridComponentReference, responseContext?: any) => {
		const { queryStore } = this.props;
		if (component) {
			const { props, queries } = component;
			if (queryStore) {
				let executionOptions = queryStore.executionOptions;
				if (responseContext) {
					executionOptions = common.commonUtils.deepMergeObjects(
						{ arrayMergeStrategy: 'DeepMerge' },
						executionOptions,
						{
							responseContext,
						},
					);
				}
				const dependencies = { queryClass: query.Query };
				const queryResults = queries
					? query.queryUtils.queryHelper.convertQueryMime(dependencies, queries, executionOptions)
					: {};
				const response = common.commonUtils.deepMergeObjectsWithOptions(
					{ arrayMergeStrategy: 'DeepMerge' },
					props || {},
					queryResults,
				);
				return response;
			}
			return props;
		}
	};
	getKey = (prefix: string, index: number, repetitionIndex = 0, name = 'NO_NAME') => {
		return `${prefix}_${index}_${repetitionIndex}_${name}`;
	};
	getElementRepetitionParams = (
		element: IGridElementBase,
		elementType: GridElement,
		repetition: IGridElementRepetition,
		parentRepetitions: IGridElementRepetition[] = [],
	) => {
		const repetitionsForChildren = [...parentRepetitions];
		if (element.repetitions) {
			repetitionsForChildren.push(...element.repetitions);
		}
		repetitionsForChildren.push(repetition);
		const repetitionProps = this.getRepetitionProps(elementType, repetitionsForChildren);
		return {
			repetitionsForChildren,
			repetitionProps,
		};
	};
	renderGrid = (grid: IGrid, gridIndex: number, parentRepetitions: IGridElementRepetition[] = []) => {
		const { rows = [], style: gridStyle, uiId, name: gridName } = grid;
		const repetitions = this.getRepetitions(grid);
		const fragmentKey = this.getKey('grid', gridIndex, -1, gridName);
		return (
			<Fragment key={fragmentKey}>
				{repetitions.map((repetition, repetitionIndex) => {
					const gridKey = this.getKey('grid', gridIndex, repetitionIndex, gridName);
					const { repetitionProps, repetitionsForChildren } = this.getElementRepetitionParams(
						grid,
						GridElement.grid,
						repetition,
						parentRepetitions,
					);
					return (
						<Wrapper data-ui-id={uiId} {...repetitionProps} key={gridKey} componentStyle={gridStyle}>
							{rows.map((row, rowIndex) => {
								return this.renderRow(row, rowIndex, repetitionsForChildren);
							})}
						</Wrapper>
					);
				})}
			</Fragment>
		);
	};
	renderRow = (row: IGridRow, rowIndex: number, parentRepetitions: IGridElementRepetition[] = []) => {
		const { name: rowName, style: rowStyle, columns = [], uiId } = row;
		const repetitions = this.getRepetitions(row);
		const fragmentKey = this.getKey('row', rowIndex, -1, rowName);
		return (
			<Fragment key={fragmentKey}>
				{repetitions.map((repetition, repetitionIndex) => {
					const rowKey = this.getKey('row', rowIndex, repetitionIndex, rowName);
					const { repetitionProps, repetitionsForChildren } = this.getElementRepetitionParams(
						row,
						GridElement.row,
						repetition,
						parentRepetitions,
					);
					return (
						<Row data-ui-id={uiId} {...repetitionProps} key={rowKey} componentStyle={rowStyle}>
							{columns.map((column, columnIndex) => {
								return this.renderColumn(column, columnIndex, repetitionsForChildren);
							})}
						</Row>
					);
				})}
			</Fragment>
		);
	};
	renderColumn = (column: IGridColumn, columnIndex: number, parentRepetitions: IGridElementRepetition[] = []) => {
		const { name: columnName, style: columnStyle, components: columnComponents = [], uiId } = column;
		const repetitions = this.getRepetitions(column);
		const fragmentKey = this.getKey('column', columnIndex, -1, columnName);
		return (
			<Fragment key={fragmentKey}>
				{repetitions.map((repetition, repetitionIndex) => {
					const columnKey = this.getKey('column', columnIndex, repetitionIndex, columnName);
					const { repetitionProps, repetitionsForChildren } = this.getElementRepetitionParams(
						column,
						GridElement.column,
						repetition,
						parentRepetitions,
					);
					return (
						<Column data-ui-id={uiId} {...repetitionProps} key={columnKey} componentStyle={columnStyle}>
							{columnComponents.map((component, componentIndex) => {
								return this.renderComponent(component, componentIndex, repetitionsForChildren);
							})}
						</Column>
					);
				})}
			</Fragment>
		);
	};
	renderComponent = (
		gridComponent: IGridComponent,
		componentIndex: number,
		parentRepetitions: IGridElementRepetition[] = [],
	) => {
		const { components, render } = this.props;
		const { components: configurationComponents } = this.configuration || ({} as IGridConfiguration);
		const allComponents = {
			...(configurationComponents || {}),
			...(components || {}),
		};
		const {
			name: componentName,
			style: componentStyle,
			grid: childGrid,
			key: key,
			children: childrenComponents,
			uiId,
		} = gridComponent;
		const repetitions = this.getRepetitions(gridComponent);
		const fragmentKey = this.getKey('component', componentIndex, -1, componentName);
		return (
			<Fragment key={fragmentKey}>
				{repetitions.map((repetition, repetitionIndex) => {
					const componentKey = this.getKey('component', componentIndex, repetitionIndex, componentName);
					const { repetitionProps, repetitionsForChildren } = this.getElementRepetitionParams(
						gridComponent,
						GridElement.component,
						repetition,
						parentRepetitions,
					);
					let child: ReactNode = null;
					if (key) {
						const gridComponentReference = allComponents[key];
						if (gridComponentReference) {
							const {
								// eslint-disable-next-line @typescript-eslint/naming-convention
								component: AppComponent,
								hide,
							} = gridComponentReference;
							if (!hide && AppComponent) {
								const componentProps = this.getComponentProps(
									gridComponent,
									gridComponentReference,
									repetitionProps,
								);
								if (componentProps) {
									const { props = {}, referenceProps = {} } = componentProps;
									let children = null;
									if (childrenComponents && isArrayLike(childrenComponents)) {
										children = childrenComponents.map((child, childIndex) => {
											return this.renderComponent(child, childIndex, repetitionsForChildren);
										});
									}
									child = (
										<AppComponent
											data-ui-id={uiId}
											{...repetitionProps}
											{...referenceProps}
											{...props}
											render={key === 'base.applicationEmbed' ? render : undefined}
										>
											{children}
										</AppComponent>
									);
								}
							}
						}
					} else if (childGrid) {
						child = this.renderGrid(childGrid, 0, repetitionsForChildren);
						// <StyledGrid data-ui-id={uiId} grid={childGrid} components={components} queryStore={queryStore} />;
					}
					return (
						<ComponentWrapper data-ui-id={uiId} key={componentKey} componentStyle={componentStyle}>
							{child}
						</ComponentWrapper>
					);
				})}
			</Fragment>
		);
	};
	getRepetitions = (element: IGridElementBase): IGridElementRepetition[] => {
		const { repeater } = element;
		let repetitions: IGridElementRepetition[] = [];
		if (!repeater) {
			// If there is no repeater we load a single value in
			repetitions.push({});
		} else {
			const { inputs = [] } = repeater;
			inputs.forEach((input) => {
				const values = this.convertToArray(this.getRepeaterInputValues(input));
				if (values) {
					const hasExistingRepetitions = repetitions.length > 0;
					const newRepetitions: IGridElementRepetition[] = [];
					values.forEach((value) => {
						if (!hasExistingRepetitions) {
							newRepetitions.push(this.loadRepetition({}, repeater, input, value));
						} else {
							repetitions.forEach((repetition) => {
								newRepetitions.push(this.loadRepetition(repetition, repeater, input, value));
							});
						}
					});
					repetitions = newRepetitions;
				}
			});
		}
		return repetitions;
	};
	convertToArray = (value: any) => {
		if (typeof value !== 'undefined' && !isArrayLike(value)) {
			return [value];
		}
		return value;
	};
	loadRepetition = (
		repetition: IGridElementRepetition,
		repeater: IGridElementRepeater,
		input: IGridElementRepeaterInput,
		value: any,
	): IGridElementRepetition => {
		const { key } = input;
		if (!repetition.inputs) {
			repetition.inputs = {};
		}
		const { inputs } = repetition;
		if (key) {
			inputs[key] = {
				key,
				value,
				input,
				repeater,
			};
		}
		return repetition;
	};
	getRepeaterInputValues = (input: IGridElementRepeaterInput) => {
		const { queryStore } = this.props;
		const { valuesQuery } = input;
		if (queryStore && valuesQuery) {
			const queryInstance = new query.Query(valuesQuery);
			return queryInstance.execute(queryStore.executionOptions);
		}
		return undefined;
	};
	getRepetitionProps = (elementType: GridElement, repetitions: IGridElementRepetition[] = []): object => {
		const props: any = {};
		repetitions.forEach((repetition) => {
			const { inputs } = repetition;
			if (inputs) {
				const inputKeys = Object.keys(inputs);
				inputKeys.forEach((key) => {
					const repetitionInput = inputs[key];
					const { value, repeater } = repetitionInput;
					if (!repeater || !repeater.targets || repeater.targets.includes(elementType)) {
						props[key] = value;
					}
				});
			}
		});
		return props;
	};
	render() {
		const { children } = this.props;
		const breakpoints = this.breakpoints;
		return (
			<Fragment>
				{breakpoints.map((breakpoint, breakpointIndex) => {
					const { props } = breakpoint;
					if (props) {
						const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
						const { grid } = props;
						if (!grid) {
							return null;
						}
						return (
							<Breakpoint {...breakpointProps}>
								{this.renderGrid(grid, 0)}
								{children}
							</Breakpoint>
						);
					}
				})}
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(observer(Grid), GRID_CATEGORY_KEY, GRID_COMPONENT_KEY);
